import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ColumnDef,
  PaginationState,
  Row,
  TableMeta,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useElementSize } from 'usehooks-ts'

import Pagination from '@/components/ui/pagination/Pagination'
import DelayedRender from '@/components/hoc/DelayedRender'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Theme,
} from '@mui/material'

import tableNoData from '@/assets/svg/table-no-data.svg'

import TextInput from '../inputs/TextInput'

type BaseData = {
  id: string
  disabled?: boolean
}

export type TableProps<T extends BaseData> = {
  'aria-label'?: string
  autoResetPage?: boolean
  container?: React.ElementType
  columns: ColumnDef<T, any>[]
  data: T[]
  disableHeader?: boolean
  disableGlobalSearch?: boolean
  disableGutters?: boolean
  highlight?: boolean
  highLightedRow?: T[]
  infiniteScroll?: boolean
  initialColumnVisibility?: VisibilityState
  loading?: boolean
  manualPagination?: boolean
  meta?: TableMeta<T>
  onDeleteMany?: (rows: T[]) => void
  onGlobalSearch?: (text: string) => void
  onPaginationChange?: (paginationState: PaginationState) => void
  onSelectRow?: (rows: T[]) => void
  rowsPerPageOptions?: number[]
  selectedRows?: T[]
  selectable?: boolean
  showPagination?: boolean
  totalRows?: number
  variant?: 'standard' | 'card'
}

const defaultRowsPerPageOptions = [5, 10, 15]

export function Table<T extends BaseData>(props: TableProps<T>) {
  const {
    'aria-label': ariaLabel,
    autoResetPage,
    container = Paper,
    columns,
    data,
    disableHeader = false,
    disableGlobalSearch = false,
    disableGutters = false,
    // highlight = false,
    // highLightedRow: highlightedRowProp,
    infiniteScroll = false,
    initialColumnVisibility = {},
    loading = false,
    manualPagination = false,
    meta,
    onDeleteMany,
    onGlobalSearch,
    onPaginationChange,
    onSelectRow,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    selectedRows: selectedRowsProp,
    selectable = false,
    showPagination = true,
    totalRows,
    variant = 'standard',
  } = props

  const [firstRowRef, { height: rowHeight }] = useElementSize()
  const { t } = useTranslation()

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialColumnVisibility)
  const [globalFilter, setGlobalFilter] = useState('')
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: rowsPerPageOptions[0],
  })
  const [internalSelectedRows, setInternalSelectedRows] = useState<T[]>([])

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  )

  const table = useReactTable<T>({
    autoResetPageIndex: autoResetPage,
    columns,
    data,
    enableExpanding: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(manualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    manualPagination,
    meta,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: showPagination ? setPagination : undefined,
    pageCount: showPagination ? Math.ceil((totalRows ?? data.length) / pagination.pageSize) : undefined,
    state: {
      columnVisibility,
      globalFilter: !onGlobalSearch ? globalFilter : undefined,
      ...(showPagination ? { pagination } : {}),
    },
  })

  const rows = table.getRowModel().rows
  const selectedRows = selectedRowsProp ?? internalSelectedRows

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = Math.max(0, pageSize - rows.length)

  const handleSelect = (row: Row<T>) => () => {
    const selectedIndex = selectedRows.findIndex((selectedRow) => selectedRow.id === row.original.id)
    let newSelected: T[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, row.original)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1))
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedRows.slice(0, selectedIndex), selectedRows.slice(selectedIndex + 1))
    }

    onSelectRow?.(newSelected)
    if (!selectedRowsProp) setInternalSelectedRows(newSelected)
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSelected = event.target.checked ? data.map((row) => row) : []
    onSelectRow?.(newSelected)
    if (!selectedRowsProp) setInternalSelectedRows(newSelected)
  }

  return (
    <TableContainer component={container}>
      {(!disableGlobalSearch || !!onDeleteMany) && (
        <Stack direction="row" p={2} borderBottom="1px solid #e0e0e0" spacing={2}>
          {!disableGlobalSearch && (
            <Box width="100%" maxWidth="300px">
              <TextInput
                fullWidth
                onChange={(event) => {
                  setGlobalFilter(event.target.value)
                  onGlobalSearch?.(event.target.value)
                }}
                value={globalFilter}
              />
            </Box>
          )}
          <Stack direction="row" flex={1} justifyContent="end">
            {!!onDeleteMany && (
              <Button disabled={!selectedRows.length} onClick={() => onDeleteMany(selectedRows)} variant="contained">
                {t('common:button.delete.label')}
              </Button>
            )}
          </Stack>
        </Stack>
      )}
      <Box sx={{ overflow: 'auto' }}>
        <MuiTable aria-label={ariaLabel} sx={[{ minWidth: 500 }, variant === 'card' && { borderCollapse: 'separate', borderSpacing: '0px 16px', px: '5px' }]}>
          {!disableHeader && (
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} sx={[variant === 'card' && { boxShadow: (theme) => theme.shadows[2], borderRadius: 2 }]}>
                  {selectable && (
                    <TableCell
                      padding="checkbox"
                      sx={[
                        !table.getVisibleLeafColumns().filter((column) => column.id !== 'actions').length && { width: '100%' },
                        variant === 'card' && { borderBottom: 'none' },
                        disableGutters && { px: 0 },
                      ]}
                    >
                      <Checkbox
                        color="primary"
                        indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
                        checked={rows.length > 0 && selectedRows.length === data.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                  )}
                  {headerGroup.headers.map((header) => {
                    const sxHeader = header.column.columnDef.meta?.sxHeader ?? header.column.columnDef.meta?.sx ?? []
                    return (
                      <TableCell
                        key={header.id}
                        align={header.column.columnDef.meta?.align}
                        scope="col"
                        sx={[...(Array.isArray(sxHeader) ? sxHeader : [sxHeader])].concat([
                          disableGutters && { px: 0 },
                          variant === 'card' && { borderBottom: 'none', bgcolor: 'primary.main', color: 'white' },
                          variant === 'card' && header.index === 0 && { borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px' },
                          variant === 'card' &&
                            header.index === headerGroup.headers.length - 1 && { borderTopRightRadius: '8px', borderBottomRightRadius: '8px' },
                        ])}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableHead>
          )}
          <TableBody sx={[{ position: 'relative' }, infiniteScroll && { '& tr:last-child td': { borderBottom: 'none' } }]}>
            {loading && (
              <DelayedRender delay={300}>
                <TableRow>
                  <TableCell sx={{ border: 'none', padding: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.7)',
                      }}
                    >
                      <CircularProgress size={20} />
                    </Box>
                  </TableCell>
                </TableRow>
              </DelayedRender>
            )}
            {rows.map((row, index) => {
              return (
                <TableRow
                  key={row.original.id || row.id}
                  ref={index === 0 ? firstRowRef : undefined}
                  sx={[
                    variant === 'card' && { boxShadow: (theme) => theme.shadows[2], borderRadius: 2 },
                    !!row.original.disabled && { color: (theme) => theme.palette.text.disabled },
                  ]}
                >
                  {selectable && (
                    <TableCell padding="checkbox" sx={[disableGutters && { px: 0 }]}>
                      <Checkbox
                        color="primary"
                        checked={selectedRows.findIndex((selectedRow) => selectedRow.id === row.original.id) !== -1}
                        disabled={row.original.disabled}
                        onChange={handleSelect(row)}
                      />
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const sxCell = cell.column.columnDef.meta?.sxCell ?? cell.column.columnDef.meta?.sx ?? []
                    return (
                      <TableCell
                        key={cell.id}
                        align={cell.column.columnDef.meta?.align}
                        className={cell.column.columnDef.meta?.className}
                        sx={[...(Array.isArray(sxCell) ? sxCell : [sxCell])].concat([
                          disableGutters && { px: 0 },
                          variant === 'card' && { borderBottom: 'none' },
                          !!row.original.disabled && { color: (theme: Theme) => theme.palette.text.disabled },
                        ])}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
            {!infiniteScroll &&
              (!data.length || !rows.length ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (selectable ? 1 : 0)}>
                    <Stack alignItems="center" justifyContent="center" p="80px 24px">
                      <img alt="no-data" src={tableNoData} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                emptyRows > 0 && (
                  <TableRow sx={{ height: (rowHeight ?? 0) * emptyRows }}>
                    <TableCell colSpan={columns.length + (selectable ? 1 : 0)} />
                  </TableRow>
                )
              ))}
          </TableBody>
        </MuiTable>
      </Box>
      {!infiniteScroll && showPagination && (
        <Box sx={{ width: '100%', p: '10px', textAlign: 'right' }}>
          <Pagination
            color="primary"
            itemsPerPageOptions={rowsPerPageOptions}
            shape="rounded"
            page={pagination.pageIndex + 1}
            onPageChange={(page) => {
              table.setPageIndex(page - 1)
              if (manualPagination) onPaginationChange?.({ pageIndex: page - 1, pageSize })
            }}
            onItemsPerPageChange={(rowsPerPage) => {
              table.setPageSize(rowsPerPage)
              if (manualPagination) onPaginationChange?.({ pageIndex: 0, pageSize: rowsPerPage })
            }}
            totalItems={globalFilter !== '' ? table.getFilteredRowModel().rows.length : totalRows ?? data.length}
          />
        </Box>
      )}
    </TableContainer>
  )
}

export default Table
